import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { Grid, TextInput } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSaveMutation } from '@/services/api';
import { formatNumberWithCommas } from '@/helpers/string';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { getMutationResult } from '@/helpers/query';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { IconInfoCircle } from '@tabler/icons-react';
import { IMaskInput } from 'react-imask';
import { notifications } from '@mantine/notifications';
import ProcessButton from '@/components/ProcessButton';
import { removeNumberFormatting } from '@/helpers/number';
import classes from './incomeLiabilities.module.css';

const IncomeLiabilities = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  const { isRecycling } = extraDetails;
  useIdInStoreGuard();

  const required = t('globals.validations.required');
  const numbers = t('globals.validations.number');

  const formValidations = {
    income: (value) => {
      if (!value || typeof value !== 'string') return required;
      // Remove commas and check if it's a valid number
      const cleanValue = value.replace(/[₪,\s]/g, '');
      if (!/^\d+$/.test(cleanValue)) return numbers;
      return null;
    },
    outcome: (value) => {
      if (!value || typeof value !== 'string') return required;
      // Remove commas and check if it's a valid number
      const cleanValue = value.replace(/[₪,\s]/g, '');
      if (!/^\d+$/.test(cleanValue)) return numbers;
      return null;
    },
  };

  const initialValues = {
    income: formatNumberWithCommas(extraDetails.income),
    outcome: formatNumberWithCommas(extraDetails.outcome),
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  // Ensure form values are always strings
  useEffect(() => {
    if (form.values.income && typeof form.values.income !== 'string') {
      form.setFieldValue('income', form.values.income.toString());
    }
    if (form.values.outcome && typeof form.values.outcome !== 'string') {
      form.setFieldValue('outcome', form.values.outcome.toString());
    }
  }, [form, extraDetails.income, extraDetails.outcome]);

  const handleNumberChange = (field) => (event) => {
    // Ensure the value is always a string
    const stringValue = formatNumberWithCommas(event.target.value);
    form.setFieldValue(field, stringValue);
  };

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }

      // Keep the values as numbers
      const formData = {
        ...form.values,
        income: removeNumberFormatting(form.values.income),
        outcome: removeNumberFormatting(form.values.outcome),
      };

      // Save data to API
      const objectToSend = {
        ...formData,
        id,
      };

      const { isError } = await getMutationResult(objectToSend, apiSave);

      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }

      // Save data to store
      dispatch(updateExtraDetails(formData));

      // Navigate to next step

      if (isRecycling === 'yes') {
        navigate(routeWithParams(getRouteById(routes.newProposal.id, 'uploadBankFile').path, { id }));
      } else {
        navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loanAmount').path, { id }));
      }
    } catch (error) {
      console.error('Error saving income and liabilities data:', error);
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'bankingConduct').path, { id }));
  };

  return (
    <ProcessHeader
      headerText="הכנסות והתחייבויות"
      headerColor="#FFF3E0"
      headerTextColor="#F57C00"
      subtitle="נא לציין את סך ההכנסות החודשיות נטו במשק הבית ואת סך ההתחייבויות החודשיות"
      showReturnButton
      onReturn={handleReturn}
      icon={IconInfoCircle}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        <Page.Section>
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <div className={classes.inputGroup}>
                <TextInput
                  withAsterisk
                  label={t('pages.incomeLiabilities.totalIncome.label')}
                  description={t('pages.incomeLiabilities.totalIncome.description')}
                  placeholder="הזינו את סך ההכנסות החודשיות"
                  key={form.key('income')}
                  {...form.getInputProps('income')}
                  onChange={handleNumberChange('income')}
                  maxLength={15}
                  component={IMaskInput}
                  scale={0}
                  min={0}
                  lazy={false}
                />
              </div>
            </Grid.Col>

          </Grid>
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <TextInput
                withAsterisk
                placeholder="הזינו את סך ההתחייבויות החודשיות"
                label={t('pages.incomeLiabilities.totalOutcome.label')}
                description={t('pages.incomeLiabilities.totalOutcome.description')}
                key={form.key('outcome')}
                {...form.getInputProps('outcome')}
                onChange={handleNumberChange('outcome')}
                maxLength={15}
                component={IMaskInput}
                scale={0}
                min={0}
                lazy={false}
              />
            </Grid.Col>
          </Grid>
        </Page.Section>
        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>
    </ProcessHeader >
  );
};

export default IncomeLiabilities;
